// Profile.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  User, Package, Heart, Star, Settings, LogOut, Edit2,
  Check, X, Calendar, Mail, MapPin, CreditCard, Truck,
  ChevronRight, Clock, Loader, Eye, RefreshCw, Lock
} from "lucide-react";

const API_BASE = process.env.VITE_API_URL || 'https://daroulmesbackend.onrender.com';

// Helper to get auth token
const getToken = () => localStorage.getItem('access_token');

// Helper to decode JWT (simple, for demo)
const decodeToken = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

// Format date
const formatDate = (dateStr, lng = 'en') => {
  const d = new Date(dateStr);
  const localeMap = { ar: 'ar-MA', fr: 'fr-FR', en: 'en-US' };
  return d.toLocaleDateString(localeMap[lng] || 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Stars component (reused)
function Stars({ rating = 0, size = 14 }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          fill={i < Math.round(rating) ? "#c8a065" : "#e2d8ca"}
          color={i < Math.round(rating) ? "#c8a065" : "#e2d8ca"}
        />
      ))}
    </span>
  );
}

// Tab Button Component
const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    data-active={active}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "14px 20px",
      width: "100%",
      border: "none",
      background: active ? "rgba(200,160,101,0.12)" : "transparent",
      borderLeft: active ? "4px solid #c8a065" : "4px solid transparent",
      fontFamily: "'Montserrat', sans-serif",
      fontSize: 13,
      fontWeight: active ? 600 : 400,
      color: active ? "#1f3d3a" : "#7a6a58",
      cursor: "pointer",
      transition: "all 0.2s",
      borderRadius: "0 8px 8px 0",
      marginBottom: 4,
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.background = "rgba(200,160,101,0.06)";
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.background = "transparent";
    }}
  >
    <Icon size={18} color={active ? "#c8a065" : "#9a8a78"} />
    {label}
  </button>
);

// Main Profile Component
export default function Profile() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("profile");
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);

  const token = getToken();
  const decoded = token ? decodeToken(token) : null;
  const userId = decoded?.userId;

  // Fetch all user data
  const fetchUserData = useCallback(async () => {
    if (!userId) {
      setError(t("profile.login_required"));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };

      // Parallel fetch
      const [userRes, ordersRes, favoritesRes, reviewsRes] = await Promise.all([
        fetch(`${API_BASE}/api/users/${userId}`, { headers }),
        fetch(`${API_BASE}/api/orders?userId=${userId}`, { headers }),
        fetch(`${API_BASE}/api/favorites?includeProduct=true`, { headers }),
        fetch(`${API_BASE}/api/reviews`, { headers }),
      ]);

      if (!userRes.ok) throw new Error(t("profile.failed_load"));
      const userData = await userRes.json();
      setUser(userData);
      setEditForm({ name: userData.name || "", email: userData.email });

      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData.data || []);
      }

      if (favoritesRes.ok) {
        const favData = await favoritesRes.json();
        // favData is array of favorite objects with product included
        setFavorites(favData);
      }

      if (reviewsRes.ok) {
        const reviewsData = await reviewsRes.json();
        setReviews(reviewsData);
      }

      setError(null);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, token, t]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error(t("profile.update_failed"));
      const updated = await res.json();
      setUser(updated);
      setEditing(false);
      setUpdateMessage({ type: "success", text: t("profile.updated_success") });
      setTimeout(() => setUpdateMessage(null), 3000);
    } catch (err) {
      setUpdateMessage({ type: "error", text: err.message });
      setTimeout(() => setUpdateMessage(null), 3000);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      setUpdateMessage({ type: "error", text: t("profile.password_mismatch") });
      return;
    }
    // Note: Your API may not have a password change endpoint; adjust accordingly.
    // For now, we'll simulate or you need to add an endpoint like PUT /api/users/:id/password
    // We'll assume it exists.
    try {
      const res = await fetch(`${API_BASE}/api/users/${userId}/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordForm.current,
          newPassword: passwordForm.new,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || t("profile.password_change_failed"));
      }
      setUpdateMessage({ type: "success", text: t("profile.password_changed") });
      setPasswordForm({ current: "", new: "", confirm: "" });
      setShowPasswordChange(false);
      setTimeout(() => setUpdateMessage(null), 3000);
    } catch (err) {
      setUpdateMessage({ type: "error", text: err.message });
      setTimeout(() => setUpdateMessage(null), 3000);
    }
  };

  // Remove favorite
  const removeFavorite = async (productId) => {
    try {
      const res = await fetch(`${API_BASE}/api/favorites/${productId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setFavorites(favorites.filter(f => f.productId !== productId));
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Delete review
  const deleteReview = async (reviewId) => {
    try {
      const res = await fetch(`${API_BASE}/api/reviews/${reviewId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setReviews(reviews.filter(r => r.id !== reviewId));
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <Loader size={40} style={styles.spinner} />
        <p>{t("profile.loading")}</p>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div style={styles.errorContainer}>
        <X size={32} color="#c0533a" />
        <h3>{t("profile.error")}</h3>
        <p>{error}</p>
        <button onClick={() => window.location.href = '/login'} style={styles.primaryButton}>
          {t("profile.go_to_login")}
        </button>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div style={styles.page} className="profile-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300&family=Montserrat:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.4s ease forwards; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

        /* ── Responsive layout ───────────────────────────────────────
           The base styles use inline style={styles.xxx} for desktop.
           These !important overrides kick in on smaller screens because
           media-query specificity alone can't beat inline styles.       */

        /* Tablet: collapse the sidebar above the main content */
        @media (max-width: 900px) {
          .profile-page         { padding: 80px 16px 24px !important; }
          .profile-container    { grid-template-columns: 1fr !important; gap: 20px !important; }
          .profile-sidebar      { position: static !important; top: auto !important; }
          .profile-profile-card { padding: 24px 16px !important; }
          .profile-main         { padding: 24px !important; }

          /* Sidebar nav → horizontal scrollable tabs */
          .profile-nav {
            flex-direction: row !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            padding: 10px 8px !important;
            gap: 4px !important;
            border-top: 1px solid rgba(200,160,101,0.18);
            scrollbar-width: thin;
            -webkit-overflow-scrolling: touch;
          }
          .profile-nav::-webkit-scrollbar { height: 4px; }
          .profile-nav::-webkit-scrollbar-thumb { background: rgba(200,160,101,0.3); border-radius: 2px; }
          .profile-nav > button {
            flex: 0 0 auto !important;
            width: auto !important;
            margin-bottom: 0 !important;
            margin-right: 4px;
            border-left: none !important;
            border-bottom: 3px solid transparent !important;
            border-radius: 8px 8px 0 0 !important;
            padding: 10px 14px !important;
            white-space: nowrap;
          }
          .profile-nav > button[data-active="true"] {
            border-left: none !important;
            border-bottom: 3px solid #c8a065 !important;
          }
          .profile-nav > .profile-logout {
            margin-top: 0 !important;
            margin-left: auto;
          }
        }

        /* Phone: tighten everything and stack multi-column content */
        @media (max-width: 600px) {
          .profile-page              { padding: 72px 12px 20px !important; }
          .profile-main              { padding: 18px !important; border-radius: 14px !important; }
          .profile-profile-card      { padding: 22px 14px !important; }
          .profile-info-grid         { grid-template-columns: 1fr !important; gap: 14px !important; }
          .profile-favorites-grid    { grid-template-columns: 1fr !important; gap: 14px !important; }
          .profile-section-header    { flex-wrap: wrap; gap: 10px; }
          .profile-section-title     { font-size: 22px !important; }
          .profile-subsection-title  { font-size: 18px !important; }

          /* Order cards: stack header + details vertically */
          .profile-order-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px;
          }
          .profile-order-header > div:first-child {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }
          .profile-order-id      { margin-right: 0 !important; }
          .profile-order-details {
            flex-direction: column !important;
            gap: 8px !important;
            font-size: 12px !important;
          }
          .profile-order-card-large { padding: 16px !important; }

          /* Form actions: full-width stacked buttons */
          .profile-form-actions {
            flex-direction: column !important;
            gap: 10px !important;
          }
          .profile-form-actions > button {
            width: 100% !important;
            padding: 14px 24px !important;
          }

          /* Avatar a bit smaller on phone so card doesn't feel top-heavy */
          .profile-avatar { width: 68px !important; height: 68px !important; font-size: 26px !important; }

          /* Toast shouldn't touch the edge */
          .profile-toast { top: 16px !important; right: 12px !important; left: 12px !important; font-size: 12px !important; padding: 10px 16px !important; }

          /* Favorite image a bit shorter on phones */
          .profile-favorite-image { height: 160px !important; }
        }
      `}</style>

      {/* Update message toast */}
      {updateMessage && (
        <div className="profile-toast" style={{
          ...styles.toast,
          background: updateMessage.type === "success" ? "#1f3d3a" : "#c0533a",
        }}>
          {updateMessage.text}
        </div>
      )}

      <div style={styles.container} className="profile-container">
        {/* Sidebar */}
        <aside style={styles.sidebar} className="profile-sidebar">
          <div style={styles.profileCard} className="profile-profile-card">
            <div style={styles.avatar} className="profile-avatar">
              {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
            </div>
            <h2 style={styles.userName}>{user.name || t("profile.user")}</h2>
            <p style={styles.userEmail}>{user.email}</p>
            <p style={styles.memberSince}>
              <Calendar size={12} style={{ marginRight: 4 }} />
              {t("profile.member_since")} {formatDate(user.createdAt, i18n.language)}
            </p>

            <div style={styles.stats}>
              <div style={styles.statItem}>
                <span style={styles.statValue}>{orders.length}</span>
                <span style={styles.statLabel}>{t("profile.orders")}</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statValue}>{favorites.length}</span>
                <span style={styles.statLabel}>{t("profile.favorites")}</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statValue}>{reviews.length}</span>
                <span style={styles.statLabel}>{t("profile.reviews")}</span>
              </div>
            </div>
          </div>

          <nav style={styles.nav} className="profile-nav">
            <TabButton active={activeTab === "profile"} onClick={() => setActiveTab("profile")} icon={User} label={t("profile.profile")} />
            <TabButton active={activeTab === "orders"} onClick={() => setActiveTab("orders")} icon={Package} label={t("profile.order_history")} />
            <TabButton active={activeTab === "favorites"} onClick={() => setActiveTab("favorites")} icon={Heart} label={t("profile.favorites")} />
            <TabButton active={activeTab === "reviews"} onClick={() => setActiveTab("reviews")} icon={Star} label={t("profile.my_reviews")} />
            <TabButton active={activeTab === "settings"} onClick={() => setActiveTab("settings")} icon={Settings} label={t("profile.account_settings")} />
            <button onClick={handleLogout} style={styles.logoutButton} className="profile-logout">
              <LogOut size={18} /> {t("profile.logout")}
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main style={styles.main} className="profile-main fade-in">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <section>
              <div style={styles.sectionHeader} className="profile-section-header">
                <h3 style={styles.sectionTitle} className="profile-section-title">{t("profile.profile_information")}</h3>
                {!editing && (
                  <button onClick={() => setEditing(true)} style={styles.iconButton}>
                    <Edit2 size={16} /> {t("common.edit")}
                  </button>
                )}
              </div>

              {editing ? (
                <form onSubmit={handleProfileUpdate} style={styles.form}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("profile.name")}</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("profile.email")}</label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formActions} className="profile-form-actions">
                    <button type="button" onClick={() => { setEditing(false); setEditForm({ name: user.name || "", email: user.email }); }} style={styles.secondaryButton}>
                      {t("common.cancel")}
                    </button>
                    <button type="submit" style={styles.primaryButton}>{t("profile.save_changes")}</button>
                  </div>
                </form>
              ) : (
                <div style={styles.infoGrid} className="profile-info-grid">
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>{t("profile.full_name")}</span>
                    <span style={styles.infoValue}>{user.name || "—"}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>{t("profile.email_address")}</span>
                    <span style={styles.infoValue}>{user.email}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>{t("profile.role")}</span>
                    <span style={styles.infoValue}>{user.role}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>{t("profile.member_since")}</span>
                    <span style={styles.infoValue}>{formatDate(user.createdAt, i18n.language)}</span>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 40 }}>
                <h4 style={styles.subsectionTitle} className="profile-subsection-title">{t("profile.recent_orders")}</h4>
                {orders.length === 0 ? (
                  <p style={styles.emptyText}>{t("profile.no_recent_orders")}</p>
                ) : (
                  <div style={styles.orderList}>
                    {orders.slice(0, 3).map(order => (
                      <div key={order.id} style={styles.orderCard}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={styles.orderId} className="profile-order-id">{t("profile.order_number", { id: order.id })}</span>
                          <span style={styles.orderDate}>{formatDate(order.createdAt, i18n.language)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                          <span>{t("profile.total")}: <strong>${order.totalAmount.toFixed(2)}</strong></span>
                          <span style={{ color: order.isConfirmed ? "#2d5550" : "#c8a065" }}>
                            {order.isConfirmed ? t("profile.confirmed") : t("profile.pending")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {orders.length > 3 && (
                  <button onClick={() => setActiveTab("orders")} style={styles.linkButton}>
                    {t("profile.view_all_orders")} <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <section>
              <h3 style={styles.sectionTitle} className="profile-section-title">{t("profile.order_history")}</h3>
              {orders.length === 0 ? (
                <div style={styles.emptyState}>
                  <Package size={48} color="#c8a065" />
                  <p>{t("profile.no_orders")}</p>
                  <button onClick={() => window.location.href = '/jewellery'} style={styles.primaryButton}>{t("profile.start_shopping")}</button>
                </div>
              ) : (
                <div style={styles.orderList}>
                  {orders.map(order => (
                    <div key={order.id} style={styles.orderCardLarge} className="profile-order-card-large">
                      <div style={styles.orderHeader} className="profile-order-header">
                        <div>
                          <span style={styles.orderId} className="profile-order-id">{t("profile.order_number", { id: order.id })}</span>
                          <span style={styles.orderDate}>{formatDate(order.createdAt, i18n.language)}</span>
                        </div>
                        <span style={{
                          ...styles.orderStatus,
                          background: order.isConfirmed ? "rgba(31,61,58,0.1)" : "rgba(200,160,101,0.1)",
                          color: order.isConfirmed ? "#1f3d3a" : "#c8a065",
                        }}>
                          {order.isConfirmed ? t("profile.confirmed") : t("profile.processing")}
                        </span>
                      </div>
                      <div style={styles.orderDetails} className="profile-order-details">
                        <div><Truck size={14} /> {t("profile.delivery")}: {order.deliveryAddress || t("profile.not_specified")}</div>
                        <div><CreditCard size={14} /> {t("profile.total")}: ${order.totalAmount.toFixed(2)}</div>
                      </div>
                      {/* Optionally display items from orderData */}
                      <details style={styles.orderItems}>
                        <summary>{t("profile.view_items")}</summary>
                        <pre style={{ fontSize: 12, marginTop: 10 }}>
                          {JSON.stringify(order.orderData, null, 2)}
                        </pre>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Favorites Tab */}
          {activeTab === "favorites" && (
            <section>
              <h3 style={styles.sectionTitle} className="profile-section-title">{t("profile.my_favorites")}</h3>
              {favorites.length === 0 ? (
                <div style={styles.emptyState}>
                  <Heart size={48} color="#c8a065" />
                  <p>{t("profile.no_favorites")}</p>
                  <button onClick={() => window.location.href = '/jewellery'} style={styles.primaryButton}>{t("profile.explore_jewellery")}</button>
                </div>
              ) : (
                <div style={styles.favoritesGrid} className="profile-favorites-grid">
                  {favorites.map(fav => {
                    const product = fav.product;
                    return (
                      <div key={fav.id} style={styles.favoriteCard}>
                        <img src={product.imageUrl || 'https://via.placeholder.com/200'} alt={product.name} style={styles.favoriteImage} className="profile-favorite-image" />
                        <div style={styles.favoriteInfo}>
                          <h4 style={styles.productName}>{product.name}</h4>
                          <p style={styles.productPrice}>${product.price.toFixed(2)}</p>
                          <div style={styles.favoriteActions}>
                            <button onClick={() => window.location.href = `/product/${product.id}`} style={styles.iconButton}>
                              <Eye size={14} /> {t("profile.view")}
                            </button>
                            <button onClick={() => removeFavorite(product.id)} style={styles.iconButtonDanger}>
                              <X size={14} /> {t("profile.remove")}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* Reviews Tab */}
          {activeTab === "reviews" && (
            <section>
              <h3 style={styles.sectionTitle} className="profile-section-title">{t("profile.my_reviews")}</h3>
              {reviews.length === 0 ? (
                <div style={styles.emptyState}>
                  <Star size={48} color="#c8a065" />
                  <p>{t("profile.no_reviews")}</p>
                </div>
              ) : (
                <div style={styles.reviewsList}>
                  {reviews.map(review => (
                    <div key={review.id} style={styles.reviewCard}>
                      <div style={styles.reviewHeader}>
                        <div>
                          <h4 style={styles.productName}>{review.product?.name || t("profile.product")}</h4>
                          <Stars rating={review.rating} size={14} />
                        </div>
                        <button onClick={() => deleteReview(review.id)} style={styles.iconButtonDanger}>
                          <X size={14} /> {t("profile.delete")}
                        </button>
                      </div>
                      <p style={styles.reviewComment}>{review.comment || t("profile.no_comment")}</p>
                      <span style={styles.reviewDate}>{formatDate(review.createdAt, i18n.language)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <section>
              <h3 style={styles.sectionTitle} className="profile-section-title">{t("profile.account_settings")}</h3>
              
              <div style={styles.settingsSection}>
                <h4 style={styles.subsectionTitle} className="profile-subsection-title">{t("profile.change_password")}</h4>
                {!showPasswordChange ? (
                  <button onClick={() => setShowPasswordChange(true)} style={styles.secondaryButton}>
                    <Lock size={14} /> {t("profile.change_password")}
                  </button>
                ) : (
                  <form onSubmit={handlePasswordChange} style={styles.form}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("profile.current_password")}</label>
                      <input
                        type="password"
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("profile.new_password")}</label>
                      <input
                        type="password"
                        value={passwordForm.new}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                        style={styles.input}
                        required
                        minLength={6}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("profile.confirm_new_password")}</label>
                      <input
                        type="password"
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.formActions} className="profile-form-actions">
                      <button type="button" onClick={() => { setShowPasswordChange(false); setPasswordForm({ current: "", new: "", confirm: "" }); }} style={styles.secondaryButton}>
                        {t("common.cancel")}
                      </button>
                      <button type="submit" style={styles.primaryButton}>{t("profile.update_password")}</button>
                    </div>
                  </form>
                )}
              </div>

              <div style={styles.settingsSection}>
                <h4 style={styles.subsectionTitle} className="profile-subsection-title">{t("profile.danger_zone")}</h4>
                <p style={{ color: "#7a6a58", marginBottom: 16 }}>{t("profile.danger_desc")}</p>
                <button style={styles.dangerButton}>{t("profile.delete_account")}</button>
                {/* Add delete account functionality if needed */}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

// Styles object
const styles = {
  page: {
    fontFamily: "Poiret One",
    // background: "#fdf8f2",
    minHeight: "100vh",
    padding: "90px 24px",
  },
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "280px 1fr",
    gap: 32,
    alignItems: "start",
  },
  sidebar: {
    background: "rgba(255,253,249,0.96)",
    border: "1px solid rgba(200,160,101,0.22)",
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 8px 36px rgba(180,140,100,0.09)",
    position: "sticky",
    top: 24,
  },
  profileCard: {
    padding: "28px 20px",
    textAlign: "center",
    borderBottom: "1px solid rgba(200,160,101,0.18)",
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1f3d3a, #2d5550)",
    color: "#fdf8f2",
    fontSize: 32,
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    border: "2px solid #c8a065",
  },
  userName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22,
    fontWeight: 500,
    color: "#1f3d3a",
    margin: "0 0 4px",
  },
  userEmail: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    color: "#7a6a58",
    marginBottom: 12,
  },
  memberSince: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 11,
    color: "#9a8a78",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  stats: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid rgba(200,160,101,0.14)",
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  statValue: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    color: "#1f3d3a",
  },
  statLabel: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: ".08em",
    color: "#9a8a78",
    textTransform: "uppercase",
  },
  nav: {
    padding: "16px 8px",
    display: "flex",
    flexDirection: "column",
  },
  logoutButton: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 20px",
    marginTop: 16,
    border: "none",
    background: "transparent",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: "#c0533a",
    cursor: "pointer",
    transition: "background 0.2s",
    borderRadius: 8,
    width: "100%",
  },
  main: {
    background: "rgba(255,253,249,0.96)",
    border: "1px solid rgba(200,160,101,0.22)",
    borderRadius: 18,
    padding: "32px",
    boxShadow: "0 8px 36px rgba(180,140,100,0.09)",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 28,
    fontWeight: 500,
    color: "#1f3d3a",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  subsectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 20,
    fontWeight: 500,
    color: "#1f3d3a",
    margin: "0 0 16px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px 32px",
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  infoLabel: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: ".1em",
    color: "#9a8a78",
    textTransform: "uppercase",
  },
  infoValue: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 15,
    color: "#1f3d3a",
  },
  form: {
    marginTop: 8,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    fontWeight: 500,
    color: "#5a4a38",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 14,
    border: "1.5px solid rgba(200,160,101,0.3)",
    borderRadius: 10,
    background: "#fff",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  formActions: {
    display: "flex",
    gap: 12,
    marginTop: 24,
  },
  primaryButton: {
    padding: "12px 24px",
    background: "#1f3d3a",
    border: "none",
    borderRadius: 8,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".06em",
    color: "#fdf8f2",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  secondaryButton: {
    padding: "12px 24px",
    background: "transparent",
    border: "1.5px solid rgba(200,160,101,0.4)",
    borderRadius: 8,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: "#7a6a58",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  iconButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: "transparent",
    border: "1px solid rgba(200,160,101,0.4)",
    borderRadius: 6,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    color: "#7a6a58",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  iconButtonDanger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: "transparent",
    border: "1px solid rgba(192,83,58,0.4)",
    borderRadius: 6,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    color: "#c0533a",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  dangerButton: {
    padding: "12px 24px",
    background: "transparent",
    border: "1.5px solid #c0533a",
    borderRadius: 8,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: "#c0533a",
    cursor: "pointer",
  },
  emptyState: {
    textAlign: "center",
    padding: "48px 0",
    color: "#7a6a58",
  },
  emptyText: {
    fontFamily: "'Montserrat', sans-serif",
    color: "#7a6a58",
  },
  orderList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  orderCard: {
    padding: "16px",
    background: "#fff",
    border: "1px solid rgba(200,160,101,0.14)",
    borderRadius: 12,
  },
  orderCardLarge: {
    padding: "20px",
    background: "#fff",
    border: "1px solid rgba(200,160,101,0.14)",
    borderRadius: 12,
    marginBottom: 16,
  },
  orderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  orderId: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    color: "#1f3d3a",
    marginRight: 16,
  },
  orderDate: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    color: "#9a8a78",
  },
  orderStatus: {
    padding: "4px 10px",
    borderRadius: 20,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: ".04em",
  },
  orderDetails: {
    display: "flex",
    gap: 24,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    color: "#5a4a38",
    marginBottom: 12,
  },
  orderItems: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    color: "#7a6a58",
    cursor: "pointer",
  },
  favoritesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 20,
  },
  favoriteCard: {
    background: "#fff",
    border: "1px solid rgba(200,160,101,0.14)",
    borderRadius: 12,
    overflow: "hidden",
  },
  favoriteImage: {
    width: "100%",
    height: 180,
    objectFit: "cover",
  },
  favoriteInfo: {
    padding: 14,
  },
  productName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 16,
    fontWeight: 500,
    color: "#1f3d3a",
    margin: "0 0 4px",
  },
  productPrice: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    color: "#c8a065",
    marginBottom: 12,
  },
  favoriteActions: {
    display: "flex",
    gap: 8,
  },
  reviewsList: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  reviewCard: {
    padding: "20px",
    background: "#fff",
    border: "1px solid rgba(200,160,101,0.14)",
    borderRadius: 12,
  },
  reviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  reviewComment: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 14,
    color: "#5a4a38",
    lineHeight: 1.6,
    margin: "10px 0",
  },
  reviewDate: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 11,
    color: "#9a8a78",
  },
  settingsSection: {
    marginBottom: 32,
  },
  linkButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginTop: 12,
    padding: "8px 0",
    background: "none",
    border: "none",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    fontWeight: 500,
    color: "#c8a065",
    cursor: "pointer",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "80vh",
    fontFamily: "'Montserrat', sans-serif",
    color: "#1f3d3a",
  },
  spinner: {
    animation: "spin 1s linear infinite",
    marginBottom: 16,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "80vh",
    fontFamily: "'Montserrat', sans-serif",
    color: "#1f3d3a",
  },
  toast: {
    position: "fixed",
    top: 24,
    right: 24,
    padding: "12px 24px",
    borderRadius: 8,
    color: "#fff",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    zIndex: 1000,
    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
    animation: "fadeIn 0.3s ease",
  },
};